import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { compose } from 'recompose'
import { join as joinPromise } from 'bluebird'
import ImmutablePropTypes from 'react-immutable-proptypes'
import floor from 'lodash/floor'
import _get from 'lodash/get'
import _isFunction from 'lodash/isFunction'
import _find from 'lodash/find'
import { Map } from 'immutable'
import { Provider, connect as connectRedux } from 'react-redux'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import VideoPlayerSpinner from '../VideoPlayerSpinner/index'
import VideoPlayerEnded from '../VideoPlayerEnded/index'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import { push as pushNewRelic } from '../../browser/new-relic/index'
import {
  getContainerStyles,
  setupVideoPlayer,
} from '../../video-js/index'
import { isIOS } from '../../environment/index'
import { TYPE_PREVIEW, VIDEO_PLAYER_INACTIVITY_TIMEOUT } from '../../services/video/index'
import { STORE_KEY_VIDEO_PLAYER_NEXT } from '../../services/store-keys/index'
import LiveVideoPlayerOverlay from './LiveVideoPlayerOverlay'

const config = getConfig()
const LIVE_PLAYER_IOS_NO_AUTOPLAY = _get(config, 'livePlayerIosNoAutoplay', 0)

/**
 * Get current quality level from player when available (e.g. MSE; not native HLS).
 * @param {Object} player - Video.js player
 * @returns {{ width?: number, height?: number, bitrate?: number } | null}
 */
function getCurrentQualityLevel (player) {
  try {
    if (!player || typeof player.qualityLevels !== 'function') return null
    const ql = player.qualityLevels()
    if (!ql || typeof ql.selectedIndex === 'undefined') return null
    const idx = ql.selectedIndex
    if (idx < 0 || !ql.length) return null
    const rep = ql[idx] || ql.getQualityLevel?.(idx)
    if (!rep) return null
    const width = rep.width ?? rep.width_
    const height = rep.height ?? rep.height_
    const bitrate = rep.bitrate ?? rep.bitrate_
    if (width == null && height == null && bitrate == null) return null
    return { width, height, bitrate }
  } catch (_) {
    return null
  }
}

/**
 * On quality level change, set NR custom attributes so later events carry rendition/bitrate.
 * @param {Object} player - Video.js player
 */
function attachQualityLevelsListener (player) {
  try {
    if (!player || typeof player.qualityLevels !== 'function') return
    const ql = player.qualityLevels()
    if (!ql || typeof ql.on !== 'function') return
    ql.on('change', () => {
      const level = getCurrentQualityLevel(player)
      if (!level) return
      pushNewRelic((newrelic) => {
        if (!newrelic || !_isFunction(newrelic.setCustomAttribute)) return
        if (level.width != null) {
          newrelic.setCustomAttribute('contentRenditionWidth', level.width)
        }
        if (level.height != null) {
          newrelic.setCustomAttribute('contentRenditionHeight', level.height)
        }
        if (level.bitrate != null) {
          newrelic.setCustomAttribute('contentBitrate', level.bitrate)
        }
      })
    })
  } catch (_) {
    // ignore
  }
}

function renderVideoPlayerApps ({ store }) {
  return (
    <Provider store={store}>
      <LiveVideoPlayerOverlay />
    </Provider>
  )
}

const convertToPlayerLanguage = {
  en: 'eng',
  es: 'spa',
  de: 'eng',
  fr: 'eng',
}

function buildVideoOpts (options = {}) {
  const {
    user = Map(),
    poster = '',
    controls = true,
    loop = false,
    playsinline = true,
  } = options
  return {
    poster,
    loop,
    playsinline,
    liveui: true,
    controls,
    inactivityTimeout: 3500,
    language: user.getIn(['data', 'language', '0'], null),
  }
}

/**
 * Initialize video JS plugins
 */
async function initVideoJsPlugins () {
  const [
    VideoPlayerAppsPlugin,
    KeyboardPlaybackTogglePlugin,
  ] = await joinPromise(
    import('../../video-js/video-player-app/index'),
    import('../../video-js/keyboard-playback-toggle/index'),
  )
  const registeredPlugins = global.videojs.getPlugins()
  if (registeredPlugins[VideoPlayerAppsPlugin.PLUGIN_NAME] === undefined) {
    VideoPlayerAppsPlugin.register(global.videojs)
  }
  if (registeredPlugins[KeyboardPlaybackTogglePlugin.PLUGIN_NAME] === undefined) {
    KeyboardPlaybackTogglePlugin.register(global.videojs)
  }
}

function dataHasError (props) {
  const { video } = props
  return (
    video.getIn(['data', '_dataError'])
  )
}

function renderVideoPlayerEnded (props) {
  const {
    videoPlayer,
    type,
    video,
    auth,
    location,
    history,
    tiles,
    getTilesData,
    deleteTiles,
    displayVideoEnded,
    setVideoPlayerEnded,
    user,
  } = props
  const videoEnded = videoPlayer.get('ended', false)
  if (displayVideoEnded && videoEnded) {
    return (
      <VideoPlayerEnded
        user={user}
        type={type}
        video={video}
        auth={auth}
        location={location}
        history={history}
        tiles={tiles}
        getTilesData={getTilesData}
        deleteTiles={deleteTiles}
        setVideoPlayerEnded={setVideoPlayerEnded}
      />
    )
  }
  return null
}

class LiveVideoPlayer extends Component {
  constructor (props) {
    super(props)
    this.state = {
      containerStyles: {},
      videoReady: false,
    }
  }

  componentDidMount () {
    const {
      props,
    } = this
    const {
      setVideoPlayerFullscreen,
    } = props

    setVideoPlayerFullscreen(false)
  }

  componentDidUpdate () {
    this.handlePlayerEndedExitFullsceen()
  }

  componentWillUnmount () {
    const { props } = this
    const {
      setVideoPlayerEnded,
      setUserVideoDataFeaturePosition,
    } = props
    if (this._onWindowResize) {
      global.removeEventListener('resize', this._onWindowResize)
    }
    if (this._player) {
      const currentTime = this.getPosition()
      setUserVideoDataFeaturePosition(currentTime)
    }
    setVideoPlayerEnded(false)
    this.disposePlayer()
  }

  onPlayerFailure = () => {
    const { setLoading } = this.props
    setLoading(false)
  }

  onPlayerSuccess = async (data) => {
    const { props } = this
    const {
      type,
      user,
      media,
      userVideo,
      playerSrc,
      setLoading,
      setMetaMoreVisible,
      location,
    } = props
    setLoading(true)
    // always set this to false when loading in the video player
    setMetaMoreVisible(false)
    await initVideoJsPlugins()
    const { ref: dataPlayer } = data
    dataPlayer.options().inactivityTimeout = VIDEO_PLAYER_INACTIVITY_TIMEOUT
    const component = this
    dataPlayer.ready(function onPlayerReady () {
      const player = setupVideoPlayer({
        // Use the player instance from the ready function
        player: this,
        media,
        type,
        user,
        userVideo,
        location,
        src: playerSrc,
        text: null,
        startAt: null,
        isLiveVideo: true,
      })
      if (!player) {
        return
      }
      component.setVideoPlayer(player)
      component.configurePlayerPlugins(player)
      component.registerPlayerEventHandlers(player)
      component.setState({ videoReady: true })
    })
  }

  getPosition = () => { // US13283, CP-2
    try {
      if (this._player) {
        return floor(this._player.currentTime())
      }
    } catch (e) {
      // Do nothing player call failed
    }
    return null
  }

  setContainerStyles = () => {
    const { props } = this
    this.setState(() => ({ containerStyles: getContainerStyles(props) }))
  }

  setVideoPlayer = (player) => {
    this._player = player
    this.forceUpdate()
  }

  disposePlayer = () => {
    if (this._newRelicTracker && _isFunction(this._newRelicTracker.dispose)) {
      this._newRelicTracker.dispose()
      this._newRelicTracker = undefined
    }
    if (!this._player) {
      return
    }
    // React player loader component will dispose player
    this._player = undefined
  }

  /**
   * Handle when the videoPlayer state of ended is true
   * and the player is in full screen mode
   */
  handlePlayerEndedExitFullsceen = () => {
    const { props, _player } = this
    const { videoPlayer } = props
    if (!_player) {
      return
    }
    try {
      if (videoPlayer.get('ended', false) && _player.isFullscreen()) {
        _player.exitFullscreen()
      }
    } catch (e) {
      // Player errored do nothing
    }
  }

  registerPlayerEventHandlers = (player) => {
    const { props } = this
    const {
      setLoading,
      onVideoEnded,
      setVideoPlayerFullscreen,
      setMetaAutoHideVisibility,
      setMetaVisible,
      user,
    } = props
    player.on('loadedmetadata', function onLoadedMetaData () {
      const playerLanguage = convertToPlayerLanguage[user.getIn(['data', 'language', 0], 'en')]
      const audioTrackList = this.audioTracks()
      const track = _find(audioTrackList, (trk) => trk.language === playerLanguage)
      if (track && !track.enabled) {
        track.enabled = true
      }
    })
    player.on('loadedmetadata', async function onLoadedMetaData () {
      const skipProgrammaticPlay = isIOS() && LIVE_PLAYER_IOS_NO_AUTOPLAY
      if (!skipProgrammaticPlay) {
        try {
          await this.play()
        } catch (_) {
          // Do nothing the brower might not allow playback
        }
      }
      setLoading(false)
    })
    // Do not clear loading on 'playing' here. On iOS (e.g. 26.3.1 webview), 'playing' can fire
    // before video is actually visible; clearing the spinner then leaves a blank/stuck screen and
    // worse UX
    // than an endless spinner. When debugging the iOS loading issue with Brightcove, consider
    // re-adding: player.one('playing', () => setLoading(false)) to help reproduce the failure.
    player.on('ended', (e) => {
      if (_isFunction(onVideoEnded)) {
        onVideoEnded(e)
      }
    })
    player.on('fullscreenchange', () => {
      const isFullscreen = document.fullscreenElement
        || document.mozFullScreenElement
        || document.webkitFullscreenElement
      setVideoPlayerFullscreen(isFullscreen)
    })
    player.on('useractive', () => {
      setMetaAutoHideVisibility(true, 'videoPlayer')
    })
    player.on('userinactive', () => {
      setMetaAutoHideVisibility(false)
    })
    player.on('play', function onPlay () {
      setMetaVisible(true)
      this.liveTracker.seekToLiveEdge()
      this.on('tap', function onTap () {
        this.userActive(!this.userActive() === true)
      })
    })
    const audioTrackList = player.audioTracks()
    audioTrackList.addEventListener('change', () => {
      // Send VideoAudioChange PageAction for New Relic (same as VOD).
      let label = 'English'
      try {
        const list = player.audioTracks && player.audioTracks()
        const enabledTrack = list ? _find(list, (trk) => trk.enabled) : null
        if (enabledTrack && enabledTrack.label) label = enabledTrack.label
      } catch (_) {
        // ignore
      }
      this.props.setEventVideoAudioChanged({ lang: label, isLive: true, isInitial: false })
    })
    // Initial language report so "unique users per language" includes users who never change track.
    player.one('playing', () => {
      let label = 'English'
      try {
        const list = player.audioTracks && player.audioTracks()
        const enabledTrack = list ? _find(list, (trk) => trk.enabled) : null
        if (enabledTrack && enabledTrack.label) label = enabledTrack.label
      } catch (_) {
        // ignore
      }
      this.props.setEventVideoAudioChanged({ lang: label, isLive: true, isInitial: true })
    })
  }

  configureNewRelicPlugin = async (player) => {
    const nrConfig = _get(config, 'newRelicVideo', {})
    const { applicationID, licenseKey } = nrConfig
    if (!applicationID || !licenseKey) {
      return
    }
    const { props } = this
    const {
      app,
      auth,
      media,
      isLive,
      analyticsMeta = {},
      title: eventTitle,
      track: eventTrack,
    } = props
    const uid = auth.get('uid', null)
    const viewerId = analyticsMeta?.viewerId ?? uid
    const contentId = analyticsMeta?.contentId ?? null
    const mediaId = !isLive && media ? media.get('id') : null
    const displayTitle = [eventTitle, eventTrack].filter(Boolean).join(' - ') || eventTitle || 'Live'
    if (player) {
      const p = player
      p.mediainfo = p.mediainfo || {}
      p.mediainfo.name = displayTitle
      if (contentId != null) p.mediainfo.id = String(contentId)
      else if (mediaId != null) p.mediainfo.id = String(mediaId)
      if (!isLive) {
        const durationSec = typeof p.duration === 'function' ? p.duration() : NaN
        if (Number.isFinite(durationSec) && durationSec > 0) {
          p.mediainfo.duration = durationSec
        }
      }
    }
    let audioTrackLabel = 'Unknown'
    try {
      const audioTrackList = player && typeof player.audioTracks === 'function' ? player.audioTracks() : []
      const track = _find(audioTrackList, (trk) => trk.enabled)
      audioTrackLabel = track ? track.label : 'English'
    } catch (e) {
      // Player errored; leave default
    }
    const beacon = nrConfig.beacon || 'bam.nr-data.net'
    try {
      const mod = await import('@newrelic/video-videojs')
      const VideojsTracker = mod.default?.default ?? mod.default
      if (typeof VideojsTracker !== 'function') {
        return
      }
      const info = { applicationID, licenseKey, beacon }
      const customData = {
        isLive: !!isLive,
        streamType: isLive ? 'live' : 'vod',
        appName: Map.isMap(app) ? app.get('name', 'web-app') : 'web-app',
        appVersion: Map.isMap(app) ? app.get('version', '0.0.0') : '0.0.0',
        playerComponent: 'LiveVideoPlayer',
        authState: Map.isMap(auth) && auth.get('jwt') ? 'logged-in' : 'anonymous',
        audioTrackLabel,
      }
      if (nrConfig.parentAppId) customData.parentAppId = nrConfig.parentAppId
      if (nrConfig.mediaAccountId) customData.mediaAccountId = nrConfig.mediaAccountId
      const brightcovePlayerId = _get(config, ['brightcove', 'liveplayer'], null)
      if (brightcovePlayerId != null && brightcovePlayerId !== '') {
        customData.brightcovePlayerId = String(brightcovePlayerId)
      }
      // Rendition/bitrate when player exposes quality levels (e.g. MSE; not native HLS).
      const qualityLevel = getCurrentQualityLevel(player)
      if (qualityLevel) {
        if (qualityLevel.width != null) customData.contentRenditionWidth = qualityLevel.width
        if (qualityLevel.height != null) customData.contentRenditionHeight = qualityLevel.height
        if (qualityLevel.bitrate != null) customData.contentBitrate = qualityLevel.bitrate
      }
      const tracker = new VideojsTracker(player, { info, customData })
      if (viewerId != null && viewerId !== '') {
        tracker.setUserId(String(viewerId))
      }
      this._newRelicTracker = tracker
      attachQualityLevelsListener(player)
    } catch (err) {
      // Tracker failed to load or initialize
    }
  }

  configureDefaultPlugins = (player) => {
    const { props } = this
    const { store } = props
    if (store) {
      player.VideoPlayerApps({
        render: (_player) => renderVideoPlayerApps(props, _player),
      })
    }
  }

  configurePlayerPlugins = (player) => {
    this.configureDefaultPlugins(player)
    this.configureNewRelicPlugin(player)
  }

  canRenderVideo = () => {
    const { props } = this
    const {
      type,
      auth,
      video,
      userVideo,
      media,
      location,
      checkPath,
      playerSrc,
    } = props

    // if playerSrc is provided, we don't need to wait for other data
    if (playerSrc) {
      return true
    }

    const processing = video.get('processing')
      || userVideo.get('processing')
      || media.get('processing')
    const pathReady = checkPath
      ? video.getIn(['data', 'path']) === location.pathname.replace(/^\//, '')
      : true
    const mediaReady = video.getIn(['data', type, 'id']) === media.getIn(['data', 'id'])
    const userVideoReady = !auth.get('jwt')
      || location.query.fullplayer === TYPE_PREVIEW
      || video.getIn(['data', 'id']) === userVideo.getIn(['data', 'id'])
    if (processing || !pathReady || !mediaReady || !userVideoReady) {
      return false
    }
    return true
  }

  render () {
    const { props, state, _player } = this
    const {
      user,
      video,
      videoPlayer,
      posterOverride,
    } = props
    const { containerStyles, videoReady } = state
    const { account, liveplayer } = _get(config, 'brightcove', {})
    const poster = posterOverride || video.getIn(['data', 'poster'], '')
    const vjsOpts = buildVideoOpts({ user, poster })
    if (dataHasError(props)) {
      return (
        <div className="video-player">
          <p className="video-player__error">
            The video player experienced an error loading this video, please
            refresh the page to try again or select another video.
          </p>
        </div>
      )
    }

    if (!videoReady && !this.canRenderVideo()) {
      return <VideoPlayerSpinner video={video} />
    }

    return (
      <div className="video-player video-player--live">
        {videoPlayer.get('loading', true) && !_player ? (
          <VideoPlayerSpinner video={video} />
        ) : null}
        {renderVideoPlayerEnded(props)}
        <div
          className="video-player__container"
          style={containerStyles}
        >
          <ReactPlayerLoader
            accountId={account}
            playerId={liveplayer}
            onSuccess={(data) => this.onPlayerSuccess(data)}
            onFailure={this.onPlayerFailure}
            options={vjsOpts}
          />
        </div>
      </div>
    )
  }
}

LiveVideoPlayer.propTypes = {
  store: PropTypes.object,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  app: ImmutablePropTypes.map.isRequired,
  page: ImmutablePropTypes.map.isRequired,
  video: ImmutablePropTypes.map.isRequired,
  media: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  tiles: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userVideo: ImmutablePropTypes.map.isRequired,
  videoPlayer: ImmutablePropTypes.map.isRequired,
  setMetaVisible: PropTypes.func.isRequired,
  setMetaMoreVisible: PropTypes.func.isRequired,
  setMetaAutoHideVisibility: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  type: PropTypes.string,
  setUserVideoDataFeaturePosition: PropTypes.func.isRequired,
  setVideoPlayerEnded: PropTypes.func.isRequired,
  deleteTiles: PropTypes.func.isRequired,
  setVideoPlayerFullscreen: PropTypes.func.isRequired,
  onVideoEnded: PropTypes.func,
  checkPath: PropTypes.bool,
  displayVideoEnded: PropTypes.bool,
  posterOverride: PropTypes.string,
  setEventDataVideoViewQualified: PropTypes.func.isRequired,
  setEventVideoAudioChanged: PropTypes.func.isRequired,
  getTilesData: PropTypes.func.isRequired,
  isLive: PropTypes.bool,
  playerSrc: PropTypes.string,
  analyticsMeta: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
    viewerId: PropTypes.string.isRequired,
    loggedIn: PropTypes.bool.isRequired,
  }),
}

LiveVideoPlayer.defaultProps = {
  checkPath: true,
  displayVideoEnded: true,
  isLive: true,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        app: state.app,
        page: state.page,
        video: state.video,
        media: state.media,
        auth: state.auth,
        tiles: state.tiles.filter((val, key) => key === STORE_KEY_VIDEO_PLAYER_NEXT),
        user: state.user,
        userVideo: state.userVideo,
        videoPlayer: state.videoPlayer,
        upstreamContext: state.upstreamContext.get('data', Map()),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDataVideoViewQualified:
          actions.eventTracking.setEventDataVideoViewQualified,
        setEventVideoAudioChanged:
          actions.eventTracking.setEventVideoAudioChanged,
        getTilesData: actions.tiles.getTilesData,
        setVideoPlayerFullscreen: actions.videoPlayer.setVideoPlayerFullscreen,
        setVideoPlayerEnded: actions.videoPlayer.setVideoPlayerEnded,
        setMetaVisible: actions.videoPlayer.setVideoPlayerMetaVisible,
        setMetaAutoHideVisibility:
          actions.videoPlayer.setVideoPlayerMetaAutoHideVisibility,
        setLoading: actions.videoPlayer.setVideoPlayerLoading,
        setMetaMoreVisible: actions.videoPlayer.setVideoPlayerMetaMoreVisible,
        setUserVideoDataFeaturePosition:
          actions.userVideo.setUserVideoDataFeaturePosition,
        deleteTiles: actions.tiles.deleteTiles,
        clearUpstreamContext: actions.upstreamContext.clearUpstreamContext,
      }
    },
  ),
)(LiveVideoPlayer)
